// Footer.jsx
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import LanguageButton from '../container/LanguageButton.js';
import MailTo from '../container/MailTo.js';

import useMessageDetailType from '../hook/useMessageDetailType.js';

import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import { getShouldShowFooter } from '../resource/getShouldShowFooter.js';
import getIsMatchedPathname from '../resource/getIsMatchedPathname.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import useBecomeCreatorLink from '../hook/useBecomeCreatorLink.js';

import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { color } from '../style/variables.js';

import DiscordIconSource from '../../img/ic_discord_white.svg';
import InstagramIconSource from '../../img/ic_instagram_white.svg';
import TwitterIconSource from '../../img/ic_twitter_white.svg';
import TelegramIconSource from '../../img/ic_telegram_white.svg';
import TiktokIconSource from '../../img/ic_tiktok_white.svg';
import YoutubeIconSource from '../../img/ic_youtube_white.svg';
import AsacpIconSource from '../../img/img_asagp.svg';
import RtaIconSource from '../../img/img_rta.svg';

const isServer = typeof window === 'undefined';

export const Footer = ({
  swagCareersUrl,
  swagBlogUrl,
  swagSelectUrl,
  swagHelpCenterUrl,
  tiktokUrl,
  youtubeUrl,
  discordUrl,
  instagramUrl,
  twitterUrl,
  telegramUrl,
  affiliateProgramUrl,
  businessCooperationUrl,
  dmcaId,
  staticContext,
  t,
}) => {
  const { pathname, search } = useLocation();
  const becomeCreatorLink = useBecomeCreatorLink();
  const trackUserClick = ({ buttonId }) => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
      },
    });
  };
  const aboutLinks = useMemo(() => {
    return [
      {
        id: 'tos',
        title: t('terms_of_service', { ns: TranslationNamespace.GENERAL }),
        to: '/tos',
        isActive: getIsMatchedPathname({ pathname, dataKey: 'isOnTos' }),
        onClick: () => {
          return trackUserClick({ buttonId: ButtonId.Setting.ButtonTos });
        },
      },
      {
        id: 'privacy',
        title: t('privacy_policy', { ns: TranslationNamespace.SETTINGS }),
        to: '/privacy',
        isActive: getIsMatchedPathname({ pathname, dataKey: 'isOnPrivacy' }),
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonPrivacyPolicy,
          });
        },
      },
      {
        id: 'debug',
        title: 'Debug',
        isActive: getIsMatchedPathname({ pathname, dataKey: 'isOnDebug' }),
        to: '/settings/more/debug',
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonDebugPanel,
          });
        },
      },
      {
        id: 'about',
        title: t('about_swag', { ns: TranslationNamespace.SETTINGS }),
        isActive: getIsMatchedPathname({ pathname, dataKey: 'isOnAbout' }),
        to: '/settings/more/about',
        onClick: () => {
          return trackUserClick({ buttonId: ButtonId.Setting.ButtonAboutSwag });
        },
      },
      {
        id: '2257',
        title: t('footer_2257', { ns: TranslationNamespace.SETTINGS }),
        to: '/settings/2257',
        isActive: getIsMatchedPathname({ pathname, dataKey: 'isOn2257' }),
        onClick: () => {
          return trackUserClick({ buttonId: ButtonId.Setting.Button2257 });
        },
      },
    ];
  }, [pathname, t]);
  const supportLinks = useMemo(() => {
    return [
      {
        id: 'faq',
        title: t('faq', { ns: TranslationNamespace.SETTINGS }),
        to: '/settings/faq',
        isActive: getIsMatchedPathname({ pathname, dataKey: 'isOnFaq' }),
        onClick: () => {
          return trackUserClick({ buttonId: ButtonId.Setting.ButtonFaq });
        },
      },
      {
        id: 'help-center',
        title: t('swag_help_center', { ns: TranslationNamespace.SETTINGS }),
        href: swagHelpCenterUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonHelpCenter,
          });
        },
      },
      {
        id: 'support',
        title: t('contact_support', { ns: TranslationNamespace.SETTINGS }),
        shouldUseMailTo: true,
        onClickCapture: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonContactSupport,
          });
        },
      },
    ];
  }, [pathname, swagHelpCenterUrl, t]);
  const workLinks = useMemo(() => {
    const links = [
      {
        id: 'career',
        title: t('job_opening', { ns: TranslationNamespace.SETTINGS }),
        href: swagCareersUrl,
        onClick: () => {
          return trackUserClick({ buttonId: ButtonId.Setting.ButtonCareer });
        },
      },
      {
        id: 'affiliate',
        title: t('footer_partner_program', {
          ns: TranslationNamespace.SETTINGS,
        }),
        href: affiliateProgramUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonSwagAffiliateProgram,
          });
        },
      },
      {
        id: 'business-cooperation',
        title: t('footer_contact_cooperation', {
          ns: TranslationNamespace.SETTINGS,
        }),
        href: businessCooperationUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonMailToMarketing,
          });
        },
      },
    ];
    if (becomeCreatorLink) {
      const { isInternalLink, link } = becomeCreatorLink;
      links.push({
        id: 'become-creator',
        title: t('swaggers_wanted', { ns: TranslationNamespace.SETTINGS }),
        ...(isInternalLink
          ? {
              to: link,
            }
          : {
              href: link,
            }),
        isActive: getIsMatchedPathname({ pathname, dataKey: 'isOnJoin' }),
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Join.ButtonBecomeCreator,
          });
        },
      });
    }
    return links.filter(({ href, to }) => !!(href || to));
  }, [
    t,
    swagCareersUrl,
    affiliateProgramUrl,
    businessCooperationUrl,
    becomeCreatorLink,
    pathname,
  ]);
  const discoverLinks = useMemo(() => {
    const links = [
      {
        id: 'blog',
        title: t('settings_blog', { ns: TranslationNamespace.SETTINGS }),
        href: swagBlogUrl,
        onClick: () => {
          return trackUserClick({ buttonId: ButtonId.Setting.ButtonSwagBlog });
        },
      },
      {
        id: 'ecommerce',
        title: t('menu_ecommerce', { ns: TranslationNamespace.SETTINGS }),
        href: swagSelectUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonHamburgerEcommerce,
          });
        },
      },
    ].filter(link => !!link.href);
    return links;
  }, [swagBlogUrl, swagSelectUrl, t]);
  const socialLinks = useMemo(() => {
    const links = [
      {
        id: 'instagram',
        icon: InstagramIconSource,
        href: instagramUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonSwagInstagram,
          });
        },
      },
      {
        id: 'youtube',
        icon: YoutubeIconSource,
        href: youtubeUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonSwagYoutube,
          });
        },
      },
      {
        id: 'twitter',
        icon: TwitterIconSource,
        href: twitterUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonSwagTwitter,
          });
        },
      },
      {
        id: 'discord',
        icon: DiscordIconSource,
        href: discordUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonSwagDiscord,
          });
        },
      },
      {
        id: 'telegram',
        icon: TelegramIconSource,
        href: telegramUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonSwagTelegram,
          });
        },
      },
      {
        id: 'tiktok',
        icon: TiktokIconSource,
        href: tiktokUrl,
        onClick: () => {
          return trackUserClick({
            buttonId: ButtonId.Setting.ButtonSwagTiktok,
          });
        },
      },
    ];
    return links.filter(({ href }) => href);
  }, [
    instagramUrl,
    youtubeUrl,
    twitterUrl,
    discordUrl,
    telegramUrl,
    tiktokUrl,
  ]);
  const renderSectionItem = item => {
    const { id, href, to, title, shouldUseMailTo } = item;
    return href ? (
      <SectionExternalLinkItem
        key={id}
        target="_blank"
        rel="noopener noreferrer"
        {...item}
      >
        {title}
      </SectionExternalLinkItem>
    ) : to ? (
      <SectionLinkItem key={id} {...item}>
        {title}
      </SectionLinkItem>
    ) : shouldUseMailTo ? (
      <SectionMailToItem key={id} {...item}>
        {title}
      </SectionMailToItem>
    ) : (
      <SectionButtonItem key={id} {...item}>
        {title}
      </SectionButtonItem>
    );
  };
  const ssrHostname = staticContext?.requestHeaders?.hostname || 'swag.live';
  const hostname = !isServer ? window.location.hostname : ssrHostname;
  const locationHref = `https://${hostname}${pathname}${search}`;
  const messageLayoutType = useMessageDetailType();
  // Should append refurl on DMCA href query string.
  // Souce code: https://images.dmca.com/Badges/DMCABadgeHelper.min.js
  const dmcaHref = `https://www.dmca.com/Protection/Status.aspx?ID=${dmcaId}&refurl=${locationHref}`;
  if (
    !getShouldShowFooter({
      pathname,
      messageLayoutType,
    })
  ) {
    return null;
  }
  return (
    <StyledFooter>
      <ContentWrapper>
        <Content>
          <Section>
            <SectionTitle>
              {t('about', { ns: TranslationNamespace.SETTINGS })}
            </SectionTitle>
            {aboutLinks.map(renderSectionItem)}
          </Section>
          <Section>
            <SectionTitle>
              {t('footer_support', { ns: TranslationNamespace.SETTINGS })}
            </SectionTitle>
            {supportLinks.map(renderSectionItem)}
          </Section>
          <Section>
            <SectionTitle>
              {t('footer_work', { ns: TranslationNamespace.SETTINGS })}
            </SectionTitle>
            {workLinks.map(renderSectionItem)}
          </Section>
          {discoverLinks.length > 0 && (
            <Section>
              <SectionTitle>
                {t('footer_discover', { ns: TranslationNamespace.SETTINGS })}
              </SectionTitle>
              {discoverLinks.map(renderSectionItem)}
            </Section>
          )}
        </Content>
        <Content isLanguageButton>
          <LanguageButton modalViewRootId={'footer-language-menu'} />
        </Content>
      </ContentWrapper>
      <SubFooterWrapper>
        <SubFooter>
          <SocialMediaLinks>
            {socialLinks.map(({ id, href, icon, onClick }) => (
              <SocialMediaLink
                key={id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
              >
                <img src={icon} alt={id} width="32" height="32" />
              </SocialMediaLink>
            ))}
          </SocialMediaLinks>
          <AssociationLinks>
            <Copyright>
              {t('footer_copyright', { ns: TranslationNamespace.SETTINGS })}
            </Copyright>
            {/* Ref. http://www.rtalabel.org/index.php?content=rtaplus&step=download */}
            <AssociationLink
              href="http://rtalabel.org/?content=validate&rating=RTA-5042-1996-1400-1577-RTA"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackUserClick({ buttonId: ButtonId.Setting.ButtonRta })
              }
            >
              <RtaIcon src={RtaIconSource} alt="RTA" width="38" height="14" />
            </AssociationLink>
            <AssociationLink
              href="https://www.asacp.org"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackUserClick({ buttonId: ButtonId.Setting.ButtonAsacp })
              }
            >
              <AsacpIcon
                src={AsacpIconSource}
                alt="ASACP"
                width="65"
                height="14"
              />
            </AssociationLink>
            <AssociationLink
              href={dmcaHref}
              title="DMCA.com Protection Status"
              target="_blank"
              className="dmca-badge"
              onClick={() =>
                trackUserClick({ buttonId: ButtonId.Setting.ButtonDmca })
              }
            >
              <DmcaIcon
                src={`https://images.dmca.com/Badges/DMCA_logo-grn-btn100w.png?ID=${dmcaId}`}
                alt="DMCA.com Protection Status"
                width="50"
                height="18"
              />
            </AssociationLink>
          </AssociationLinks>
        </SubFooter>
      </SubFooterWrapper>
    </StyledFooter>
  );
};

Footer.propTypes = {
  swagCareersUrl: PropTypes.string,
  swagBlogUrl: PropTypes.string,
  swagSelectUrl: PropTypes.string,
  swagHelpCenterUrl: PropTypes.string,
  tiktokUrl: PropTypes.string,
  youtubeUrl: PropTypes.string,
  discordUrl: PropTypes.string,
  instagramUrl: PropTypes.string,
  twitterUrl: PropTypes.string,
  telegramUrl: PropTypes.string,
  affiliateProgramUrl: PropTypes.string,
  businessCooperationUrl: PropTypes.string,
  dmcaId: PropTypes.string,
  location: PropTypes.object,
  staticContext: PropTypes.object,
  t: PropTypes.func.isRequired,
};

Footer.defaultProps = {
  swagCareersUrl: '',
  swagBlogUrl: '',
  swagSelectUrl: '',
  swagHelpCenterUrl: '',
  tiktokUrl: '',
  youtubeUrl: '',
  discordUrl: '',
  instagramUrl: '',
  twitterUrl: '',
  telegramUrl: '',
  affiliateProgramUrl: '',
  businessCooperationUrl: '',
  dmcaId: '',
  location: {},
  staticContext: {},
};

const StyledFooter = styled.footer``;

const ContentWrapper = styled.div`
  background-color: ${color.neutral[10]};
  padding-top: 68px;
  padding-bottom: 20px;
  ${media.mobile`
    padding-top: 64px;
    padding-bottom: 40px;
  `};
`;

const ContentMaxWidth = css`
  margin-left: auto;
  margin-right: auto;
  max-width: 960px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${media.tablet`
    max-width: 720px;
  `};
  ${media.mobile`
    width: 100%;
    max-width: none;
    flex-direction: column;
    justify-content: center;
  `};
`;

const Content = styled.div.attrs(({ isLanguageButton }) => ({
  style: {
    paddingLeft: `${isLanguageButton ? 0 : 16}px`,
    paddingRight: `${isLanguageButton ? 0 : 16}px`,
  },
}))`
  ${ContentMaxWidth};
  margin-bottom: 60px;
  align-items: flex-start;
  ${media.mobile`
    margin-bottom: 56px;
    align-items: center;
  `};
  :last-child {
    margin-bottom: 0px;
  }
`;

const Section = styled.div`
  max-width: 220px;
  ${media.tablet`
    max-width: 180px;
  `};
  ${media.mobile`
    margin-bottom: 60px;
    max-width: none;
    text-align: center;
    :last-child {
      margin-bottom: 0px;
    }
  `};
`;

const SectionTitle = styled.div`
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
  color: ${color.neutral[100]};
`;

const SectionItemStyle = css`
  margin-bottom: 12px;
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: ${({ isActive }) => (isActive ? color.tealBlue : color.neutral[75])};
  text-align: left;
  :last-child {
    margin-bottom: 0px;
  }
  @media (hover: hover) {
    :hover {
      color: ${color.tealBlue};
      opacity: 1;
    }
  }
  ${media.mobile`
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  `};
`;

const SectionMailToItem = styled(MailTo)`
  ${SectionItemStyle};
`;

const SectionButtonItem = styled.button`
  ${ResetButtonStyle};
  ${SectionItemStyle};
`;

const SectionExternalLinkItem = styled.a`
  ${SectionItemStyle};
`;

const SectionLinkItem = styled(({ isActive, ...restProps }) => (
  <Link {...restProps} />
))`
  ${SectionItemStyle};
`;

const SubFooterWrapper = styled.div`
  background-color: ${color.neutral[0]};
  padding-top: 32px;
  padding-bottom: 80px;
  ${media.mobile`
    padding: 40px 28px 60px;
  `};
`;

const SubFooter = styled.div`
  ${ContentMaxWidth};
`;

const SocialMediaLinks = styled.div`
  ${media.mobile`
    margin-bottom: 40px;
  `};
`;

const SocialMediaLink = styled.a`
  margin-right: 16px;
  > img {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }
  :last-child {
    margin-right: 0px;
  }
`;

const AssociationLinks = styled.div`
  display: flex;
  align-items: center;
`;

const Copyright = styled.div`
  margin-right: 8px;
  font-size: 12px;
  font-weight: 400;
  color: ${color.neutral[55]};
`;

const AssociationLink = styled.a`
  margin-right: 8px;
  :last-child {
    margin-right: 0px;
  }
`;

const RtaIcon = styled.img`
  width: 38px;
  height: 14px;
  object-fit: contain;
`;

const AsacpIcon = styled.img`
  width: 65px;
  height: 14px;
  object-fit: contain;
`;

const DmcaIcon = styled.img`
  width: 50px;
  height: 18px;
  object-fit: contain;
`;

export default withTranslation()(Footer);
